import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { map } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';

import { MENU_ITEMS, ROUTES, languageList, Languages, MenuItem } from '@shared/models';
import { ButtonComponent, SelectComponent } from '@shared/components';
import { CountriesService, LanguageService, SidebarOverlayService } from '@shared/services';
import { CapitalizeFirstPipe } from '@shared/pipes';

import { NavigationService } from '../../services/navigation.service';
import { NavigationMobileComponent } from './navigation-mobile/navigation-mobile.component';
import { MenuBurgerComponent } from './menu-burger/menu-burger.component';

@Component({
  selector: 'rp-home-header',
  standalone: true,
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    ReactiveFormsModule,
    SelectComponent,
    TranslateModule,
    MenuBurgerComponent,
  ],
  providers: [SidebarOverlayService, CapitalizeFirstPipe],
})
export class HeaderComponent {
  readonly menuItems = MENU_ITEMS;
  readonly capitalizeFirstPipe = new CapitalizeFirstPipe();

  languages: string[] = languageList.map(lang => this.capitalizeFirstPipe.transform(lang, true));
  languageControl: FormControl<string>;

  private readonly _countriesService = inject(CountriesService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _languageService = inject(LanguageService);
  private readonly _navigationService = inject(NavigationService);
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _sideBarOverlayService = inject(SidebarOverlayService);

  constructor() {
    this._setLang();

    this.languageControl.valueChanges
      .pipe(
        map(lang => lang.toUpperCase() as Languages),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe(language => {
        this._languageService.setLanguage(language);
        this._countriesService.registerLocale(language);
      });
  }

  navigateToSignUp(): void {
    this._router.navigate([ROUTES.signUp]);
  }

  navigateToSignIn(): void {
    this._router.navigate([ROUTES.auth, ROUTES.signIn]);
  }

  onNavigateToItem(item: MenuItem): void {
    const isStaticPage = this._route.snapshot.url.some(segment =>
      ['privacy-policy', 'terms-and-conditions'].includes(segment.path),
    );

    if (isStaticPage) {
      this._router.navigate(['/'], { queryParams: { section: item.sectionId } });
    } else {
      this._navigationService.navigateToItem(item.sectionId, true);
    }
  }

  onToggleMobileMenu(): void {
    this._sideBarOverlayService.open(NavigationMobileComponent);
  }

  private _setLang(): void {
    const currentLanguage = this._languageService.getCurrentLanguage();

    this.languageControl = new FormControl<string>(
      this.capitalizeFirstPipe.transform(currentLanguage, true),
    );
  }
}
